import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '@/contexts/ThemeContext';
import { getApiUrl } from '@/config/api';

interface TerraWidgetResponse {
    url: string;
    reference_id: string;
}

/**
 * ConnectTerra component handles Terra widget integration for device authentication.
 */
const ConnectTerra: React.FC = () => {
    const navigate = useNavigate();
    const { isDark } = useTheme();
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [userInfo, setUserInfo] = useState<{ username: string; role?: string; full_name?: string; first_name?: string } | null>(null);
    const [popupWindow, setPopupWindow] = useState<Window | null>(null);

    useEffect(() => {
        // Check if user is logged in
        const token = localStorage.getItem('access');
        if (!token) {
            navigate('/login');
            return;
        }

        // Get user info from localStorage
        const userInfoString = localStorage.getItem('user_info');
        if (userInfoString) {
            try {
                const user = JSON.parse(userInfoString);
                setUserInfo(user);
            } catch (err) {
                console.error('Error parsing user info:', err);
            }
        }
    }, [navigate]);

    const generateWidgetSession = async () => {
        try {
            setLoading(true);
            setError('');
            const token = localStorage.getItem('access');
            if (!token) {
                navigate('/login');
                return;
            }

            const response = await fetch(getApiUrl('/api/terra/generate-widget-session/'), {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ lang: 'en' }),
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.error || 'Failed to generate widget session');
            }

            const data: TerraWidgetResponse = await response.json();
            
            // Open widget in popup window instead of iframe (to avoid X-Frame-Options issues)
            const width = 600;
            const height = 700;
            const left = (window.screen.width - width) / 2;
            const top = (window.screen.height - height) / 2;
            
            const popup = window.open(
                data.url,
                'TerraWidget',
                `width=${width},height=${height},left=${left},top=${top},resizable=yes,scrollbars=yes`
            );
            
            if (!popup) {
                setError('Popup blocked. Please allow popups for this site.');
                setLoading(false);
                return;
            }
            
            setPopupWindow(popup);
            
            // Listen for popup to close (user completed or cancelled)
            const checkClosed = setInterval(() => {
                if (popup.closed) {
                    clearInterval(checkClosed);
                    setPopupWindow(null);
                    setLoading(false);
                    // Optionally refresh connection status or show success message
                }
            }, 500);
            
        } catch (err: any) {
            setError(err.message || 'Failed to generate widget session');
            setLoading(false);
        }
    };

    return (
        <div className="cgm-bg-primary min-h-screen flex flex-col items-center justify-center py-8 px-4 sm:px-6 lg:px-8 transition-colors duration-300">


            <div className="max-w-3xl w-full space-y-6">
                {/* Header */}
                <div className="text-center">
                    <h2 className="cgm-text-primary text-3xl font-light tracking-tight mb-2">
                        Connect Your Device via Terra
                    </h2>
                    <p className="cgm-text-secondary text-sm">
                        Connect your wearable device using Terra's unified API to access your health and fitness data.
                    </p>
                </div>

                {/* Error Display */}
                {error && (
                    <div className="cgm-alert-error px-4 py-3 rounded-md mb-4 text-sm">
                        {error}
                    </div>
                )}

                {/* Success/Status Display */}
                {popupWindow && !popupWindow.closed && (
                    <div className="cgm-card p-6 text-center">
                        <p className="cgm-text-secondary mb-4">
                            Please complete the authentication in the popup window.
                        </p>
                        <p className="cgm-text-secondary text-sm">
                            If the popup didn't open, check your browser's popup blocker settings.
                        </p>
                    </div>
                )}

                {/* Button to open widget */}
                {!popupWindow || popupWindow.closed ? (
                    <div className="cgm-card p-6 text-center">
                        <p className="cgm-text-secondary mb-6">
                            Click the button below to open the Terra widget and connect your device.
                        </p>
                        <button
                            onClick={generateWidgetSession}
                            disabled={loading}
                            className={`cgm-button px-6 py-3 rounded-xl font-semibold transition-all duration-200 ${
                                loading ? 'opacity-50 cursor-not-allowed' : ''
                            }`}
                        >
                            {loading ? 'Loading...' : 'Open Terra Widget'}
                        </button>
                    </div>
                ) : null}
            </div>
        </div>
    );
};

export default ConnectTerra;

